import { forwardRef } from 'react';
import { cn } from '../../lib/utils';

/**
 * Shared input primitive. Section E.4 of
 * docs/design-system/HPOS-V2-VISUAL-SYSTEM.md is the specification; the classes
 * below are ported verbatim from
 * artifacts/high-performer-os/src/components/ui/input.tsx.
 *
 * `text-base md:text-sm` is deliberate rather than cosmetic: 16px on mobile is
 * what stops iOS Safari zooming the viewport when the field takes focus
 * (section D.7).
 *
 * `focus-visible:ring-1 focus-visible:ring-ring` is mandatory (section E.7).
 */
export type InputProps = React.ComponentProps<'input'>;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      className={cn(
        'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 ' +
          'text-base shadow-sm transition-colors ' +
          'file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground ' +
          'placeholder:text-muted-foreground focus-visible:outline-none ' +
          'focus-visible:ring-1 focus-visible:ring-ring ' +
          'disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = 'Input';
